/**
 * AttorneyWorkDesk — Consolidated attorney-facing WorkDesk surface
 *
 * Consolidates three prior surfaces into one canonical lens:
 *   - AttorneyDashboard (Released-Cases, Submitted-Clients tabs, New-Client modal, stats, deadline alerts)
 *   - AttorneyDashboardNew/Portfolio (metric grid, cases table, generators sidebar, deadlines)
 *   - AttorneyQueue (triage stats, case list with quick actions, sidebar)
 *
 * Route: /attorney/workdesk
 * Design spec: docs/plans/2026-06-25-redesign-design-spec.md
 * Surface atlas: docs/plans/2026-06-26-redesign-surface-atlas.md §1
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { api } from '@/utils/api';
import { getAuthHeaders } from '@/lib/authHeaders';
import { useRecents } from '@/hooks/useRecents';
import { DataTable } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { MetricCard } from '@/components/shared/MetricCard';
import { BRAND_NAME } from '@/config/brand';
import {
  Briefcase,
  Clock,
  AlertCircle,
  TrendingUp,
  Users,
  DollarSign,
  Pin,
  History,
  Loader2,
  Search,
  X,
  Filter,
  RefreshCw,
  ChevronRight,
  UserPlus,
  Scale,
  FileText,
  BookOpen,
  FolderOpen,
  ExternalLink,
  Bell,
  CheckCircle2,
  AlertTriangle,
  FileSignature,
} from 'lucide-react';
import WorkDeskUtilityBar from '../../components/workdesk/WorkDeskUtilityBar';
import HandoffLine from '../../components/handoff/HandoffLine';
import type { CaseHandoff } from '../../services/handoffApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AttorneyCase {
  id: string;
  handoff?: CaseHandoff | null;
  case_id: string;
  client_name: string;
  business_name?: string;
  law_firm?: string;
  event_type?: string;
  event_date?: string;
  status: string;
  status_label: string;
  claim_amount: number;
  claim_amount_formatted: string;
  days_open: number;
  next_action: string;
  settlement_position?: {
    demand: number;
  };
  is_ready_for_review?: boolean;
  transactions_need_review?: number;
  completion_percentage?: number;
  days_stale?: number;
  pending_classifications?: number;
  // Quick-action URLs
  wizard_url?: string | null;
  reconciliation_url?: string | null;
  narrator_url?: string | null;
  report_url?: string | null;
  documents_url?: string | null;
  primary_action_url?: string | null;
  // Whether the report has been released to the firm. False = draft
  // ("In preparation"): the case is still viewable (GET /attorney/case/:id
  // resolves drafts, firm-scoped); only the report itself is a draft.
  is_released?: boolean;
  /** Computed in the attorney analysis — drives the Demands Outstanding metric. */
  ready_for_demand?: boolean;
  case_strength_score?: number | null;
  claim_value_label?: string | null;
  claim_value_source?: string | null;
}

interface PortfolioSummary {
  total_cases: number;
  cases_ready_for_demand: number;
  cases_in_negotiation: number;
  total_demand_value: number;
  average_case_age_days: number;
}

interface DeadlineAlert {
  case_id: string;
  client_name: string;
  deadline_type: string;
  deadline_date: string;
  days_remaining: number;
  // The backend's DeadlineAlertItem emits critical | warning | INFO; 'normal'
  // is not a value it can produce. Accept both so an "info" alert renders
  // instead of falling through to the default style with a type mismatch.
  urgency: 'critical' | 'warning' | 'info' | 'normal';
}

interface SubmittedClient {
  id: string;
  claimant_name: string;
  client_email: string;
  business_name?: string;
  event_type?: string;
  status: 'submitted' | 'processing' | 'completed' | string;
  created_at: string;
}

type TabOption = 'cases' | 'clients';
type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest' | 'name';
type FilterOption = 'all' | 'fire' | 'water' | 'storm' | 'other';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface MetricRailProps {
  metrics: Array<{
    key: string;
    label: string;
    // React.ReactNode (not just string | number) so money metrics can pass a
    // <FinancialValue> element instead of a hand-formatted string — see the
    // `settlement_pipeline` metric below.
    value: React.ReactNode;
    subtitle?: string;
    accent?: boolean;
    isMoney?: boolean;
  }>;
  isLoading: boolean;
}

// NOTE (bug #6, 2026-07-22): metrics here are plain, non-interactive stats.
// `accent` is purely a visual "needs action" cue (orange left-border in
// MetricCard) and must NOT be treated as a proxy for clickability — a
// previous version wired accent-true cards to navigate('/queue/attorney'),
// but that route immediately redirects back to this same page (see
// AttorneyQueueRedirect in App.tsx), making the click a no-op. Do not
// reintroduce an onClick here without an existing, real filter target to
// land on — the WorkDesk's `filterBy` state only filters by event type
// (fire/water/storm/other), it has no status-based axis to hook a
// "demands outstanding" click into without inventing a new filter system.
function MetricRail({ metrics, isLoading }: MetricRailProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-4 gap-4" data-testid="metric-rail-loading">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-[13px] border border-border bg-surface p-5 h-28 animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-4 gap-4" data-testid="metric-rail">
      {metrics.map((metric) => (
        <MetricCard
          key={metric.key}
          label={metric.label}
          value={metric.value}
          subtitle={metric.subtitle}
          accent={metric.accent}
          testId="workdesk-metric-card"
        />
      ))}
    </div>
  );
}

interface DeadlineAlertsProps {
  alerts: DeadlineAlert[];
  onDismiss?: (alert: DeadlineAlert) => void;
  onNavigate?: (caseId: string) => void;
}

function DeadlineAlerts({ alerts, onDismiss, onNavigate }: DeadlineAlertsProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const handleDismiss = (alert: DeadlineAlert) => {
    const key = `${alert.case_id}-${alert.deadline_type}`;
    setDismissed((prev) => new Set(prev).add(key));
    onDismiss?.(alert);
  };

  const visibleAlerts = alerts.filter(
    (a) => !dismissed.has(`${a.case_id}-${a.deadline_type}`)
  );

  if (visibleAlerts.length === 0) return null;

  const getUrgencyStyles = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return {
          container: 'border-status-active/30 bg-status-active/5',
          icon: 'text-status-active',
          badge: 'bg-status-active/20 text-status-active',
        };
      case 'warning':
        return {
          container: 'border-brand/30 bg-brand/5',
          icon: 'text-brand',
          badge: 'bg-brand/20 text-brand',
        };
      default:
        return {
          container: 'border-border-sub bg-elevated',
          icon: 'text-text-muted',
          badge: 'bg-surface text-text-muted',
        };
    }
  };

  const getDeadlineLabel = (type: string): string => {
    switch (type) {
      case 'carrier_response':
        return 'Carrier Response Due';
      case 'mediation_window':
        return 'Mediation Window';
      case 'demand_due':
        return 'Demand Due';
      default:
        return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-2" data-testid="deadline-alerts">
      <div className="flex items-center gap-2 mb-2">
        <Bell className="w-4 h-4 text-text-muted" />
        <h3 className="text-sm font-semibold text-text-secondary">Upcoming Deadlines</h3>
      </div>
      {visibleAlerts.map((alert, idx) => {
        const styles = getUrgencyStyles(alert.urgency);
        return (
          <div
            key={`${alert.case_id}-${alert.deadline_type}-${idx}`}
            className={`flex items-center justify-between px-4 py-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${styles.container}`}
            onClick={() => onNavigate?.(alert.case_id)}
            data-testid={`deadline-alert-${alert.case_id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${styles.icon}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate text-text-primary">
                  {alert.client_name} — {getDeadlineLabel(alert.deadline_type)}
                </p>
                <p className="text-xs text-text-muted">Due {formatDate(alert.deadline_date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${styles.badge}`}
              >
                {alert.days_remaining}d left
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismiss(alert);
                }}
                className="text-text-dim hover:text-text-secondary transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface GeneratorsSidebarProps {
  cases: AttorneyCase[];
  onNavigate: (route: string, caseId?: string) => void;
}

const GENERATORS = [
  { icon: '§', label: 'Demand Letter', route: 'demand-letter' },
  { icon: '◰', label: 'Proof of Loss', route: 'proof-of-loss' },
  { icon: '⚖', label: 'EUO Prep Packet', route: 'euo-prep-memo' },
  { icon: '✎', label: 'RoR Response', route: 'ror-response' },
  { icon: '◳', label: 'RoR Analysis', route: 'ror-analysis' },
  { icon: '◷', label: 'BI Timeline', route: 'timeline' },
];

function generatorCaseLabel(c: AttorneyCase): string {
  // client_name is required on AttorneyCase; business_name is an optional qualifier.
  return c.business_name ? `${c.client_name} — ${c.business_name}` : c.client_name;
}

function GeneratorsSidebar({ cases, onNavigate }: GeneratorsSidebarProps) {
  const hasCases = cases.length > 0;

  // SAFETY: generators must target an EXPLICITLY chosen case. Previously every
  // generator routed to cases[0] ("All cases" label) — a demand letter / EUO
  // packet could be produced for the wrong client with no picker and no confirm.
  // Default to no selection; auto-select only when there is exactly one case
  // (no ambiguity, so no wrong client is possible).
  const [selectedCaseId, setSelectedCaseId] = useState<string>(
    cases.length === 1 ? cases[0].case_id : '',
  );

  // Keep the auto-selection correct if the single case changes identity, and
  // clear a stale selection that no longer exists in the list.
  useEffect(() => {
    if (cases.length === 1) {
      setSelectedCaseId(cases[0].case_id);
    } else if (selectedCaseId && !cases.some((c) => c.case_id === selectedCaseId)) {
      setSelectedCaseId('');
    }
  }, [cases, selectedCaseId]);

  const selectedCase = cases.find((c) => c.case_id === selectedCaseId);
  const canGenerate = hasCases && !!selectedCaseId;

  return (
    <div
      className="rounded-[13px] border border-border bg-surface overflow-hidden"
      data-testid="generators-sidebar"
    >
      <div className="px-4 py-3 border-b border-border-sub flex items-center justify-between gap-2">
        <h3 className="font-serif text-base text-text-primary">Generators</h3>
        <span
          className="text-xs text-text-muted bg-elevated border border-border rounded px-2 py-1 truncate max-w-[55%]"
          data-testid="generators-target"
          title={selectedCase ? generatorCaseLabel(selectedCase) : undefined}
        >
          {selectedCase ? generatorCaseLabel(selectedCase) : 'Select a case'}
        </span>
      </div>

      <div className="p-3 space-y-3">
        <div>
          <label
            htmlFor="generator-case-select"
            className="block text-xs text-text-muted mb-1"
          >
            Target case
          </label>
          <select
            id="generator-case-select"
            value={selectedCaseId}
            onChange={(e) => setSelectedCaseId(e.target.value)}
            disabled={!hasCases}
            data-testid="generator-case-select"
            className="w-full bg-elevated border border-border rounded-[10px] px-3 py-2 text-sm text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">
              {hasCases ? 'Select a case…' : 'No cases available'}
            </option>
            {cases.map((c) => (
              <option key={c.case_id} value={c.case_id}>
                {generatorCaseLabel(c)}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {GENERATORS.map((g) => (
            <button
              key={g.route}
              onClick={() => canGenerate && onNavigate(g.route, selectedCaseId)}
              disabled={!canGenerate}
              title={
                canGenerate
                  ? `${g.label} for ${selectedCase ? generatorCaseLabel(selectedCase) : ''}`
                  : 'Select a target case first'
              }
              className={`
                bg-elevated border border-border rounded-[10px] p-3
                flex flex-col gap-2 min-h-[78px]
                transition-all duration-200
                ${canGenerate ? 'hover:border-brand cursor-pointer' : 'opacity-50 cursor-not-allowed'}
              `}
              data-testid={`generator-${g.route}`}
            >
              <span className="text-[15px] text-text-muted">{g.icon}</span>
              <span className="text-xs text-text-primary font-medium leading-tight">{g.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

interface RecentsRailProps {
  recents: ReturnType<typeof useRecents>['recents'];
  pinned: ReturnType<typeof useRecents>['pinned'];
  onPin: (item: ReturnType<typeof useRecents>['recents'][0]) => void;
  onUnpin: (id: string) => void;
  isLoading: boolean;
}

function RecentsRail({ recents, pinned, onPin, onUnpin, isLoading }: RecentsRailProps) {
  const navigate = useNavigate();

  const handleItemClick = (href?: string) => {
    if (href) navigate(href);
  };

  if (isLoading) {
    return (
      <div className="w-72 flex-shrink-0 space-y-6">
        <div className="rounded-[13px] border border-border bg-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <Loader2 className="w-4 h-4 animate-spin text-brand" />
            <span className="text-sm text-text-muted">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-72 flex-shrink-0 space-y-6">
      {/* Pinned Section */}
      <div className="rounded-[13px] border border-border bg-surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <Pin className="w-4 h-4 text-brand" />
          <h3 className="text-sm font-medium text-text-primary">Pinned</h3>
        </div>

        {pinned.length === 0 ? (
          <p className="text-sm text-text-dim">No pinned items</p>
        ) : (
          <div className="space-y-2">
            {pinned.map((item) => (
              <div
                key={item.id}
                className="group flex items-center justify-between p-2 rounded-lg hover:bg-hover cursor-pointer transition-colors"
                onClick={() => handleItemClick(item.href)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Pin className="w-3 h-3 text-brand flex-shrink-0" />
                  <span className="text-sm text-text-secondary truncate">{item.label}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnpin(item.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-text-dim hover:text-text-secondary transition-opacity"
                  aria-label="Unpin"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recents Section */}
      <div className="rounded-[13px] border border-border bg-surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4 text-text-muted" />
          <h3 className="text-sm font-medium text-text-primary">Recents</h3>
        </div>

        {recents.length === 0 ? (
          <p className="text-sm text-text-dim">No recent activity</p>
        ) : (
          <div className="space-y-1">
            {recents.slice(0, 8).map((item) => (
              <div
                key={item.id}
                className="group flex items-center justify-between p-2 rounded-lg hover:bg-hover cursor-pointer transition-colors"
                onClick={() => handleItemClick(item.href)}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-sm text-text-secondary truncate">{item.label}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPin(item);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-text-dim hover:text-brand transition-opacity flex-shrink-0"
                  aria-label="Pin"
                >
                  <Pin className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface NewClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function NewClientModal({ isOpen, onClose, onSuccess }: NewClientModalProps) {
  const [form, setForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    business_name: '',
    business_type: '',
    event_type: '',
    event_date: '',
    notes: '',
    send_email: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const authHeaders = await getAuthHeaders();
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/clients`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to submit client');
      }

      const data = await response.json();
      setSuccess(data.message);
      onSuccess?.();

      setTimeout(() => {
        onClose();
        setSuccess('');
        setForm({
          client_name: '',
          client_email: '',
          client_phone: '',
          business_name: '',
          business_type: '',
          event_type: '',
          event_date: '',
          notes: '',
          send_email: true,
        });
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit client');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-surface border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand to-brand-hover px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-3">
            <UserPlus className="w-6 h-6 text-base" />
            <h2 className="text-xl font-bold text-base">Submit New Client</h2>
          </div>
          <button onClick={onClose} className="text-base/80 hover:text-base transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Messages */}
        {success && (
          <div className="mx-6 mt-4 p-4 bg-done/10 border border-done/30 rounded-lg flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-done flex-shrink-0" />
            <p className="text-done">{success}</p>
          </div>
        )}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-status-active/10 border border-status-active/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-status-active flex-shrink-0" />
            <p className="text-status-active">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Client Name <span className="text-status-active">*</span>
            </label>
            <input
              type="text"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              className="w-full px-4 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
              placeholder="John Smith"
              required
              data-testid="new-client-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Client Email <span className="text-status-active">*</span>
            </label>
            <input
              type="email"
              value={form.client_email}
              onChange={(e) => setForm({ ...form, client_email: e.target.value })}
              className="w-full px-4 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
              placeholder="client@email.com"
              required
              data-testid="new-client-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Client Phone
            </label>
            <input
              type="tel"
              value={form.client_phone}
              onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
              className="w-full px-4 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Business Name
            </label>
            <input
              type="text"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="w-full px-4 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
              placeholder="Acme Restaurant LLC"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Business Type
              </label>
              <select
                value={form.business_type}
                onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                className="w-full px-4 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
              >
                <option value="">Select...</option>
                <option value="Restaurant">Restaurant / Food Service</option>
                <option value="Retail">Retail Store</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Healthcare">Healthcare / Medical</option>
                <option value="Professional Services">Professional Services</option>
                <option value="Construction">Construction</option>
                <option value="Hospitality">Hotel / Hospitality</option>
                <option value="Auto">Auto Dealership / Repair</option>
                <option value="Agriculture">Agriculture / Farming</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Event Type
              </label>
              <select
                value={form.event_type}
                onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                className="w-full px-4 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
              >
                <option value="">Select...</option>
                <option value="Fire">Fire</option>
                <option value="Water Damage">Water Damage</option>
                <option value="Hurricane">Hurricane</option>
                <option value="Tornado">Tornado</option>
                <option value="Flood">Flood</option>
                <option value="Earthquake">Earthquake</option>
                <option value="Business Interruption">Business Interruption</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Event Date
            </label>
            <input
              type="date"
              value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              className="w-full px-4 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
              rows={3}
              placeholder="Any additional information about the case..."
            />
          </div>

          <div className="bg-brand/5 rounded-lg p-4 border border-brand/20">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_email}
                onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                className="w-5 h-5 mt-0.5 rounded border-border text-brand focus:ring-brand"
              />
              <div>
                <span className="font-medium text-text-primary">Send portal link to client</span>
                <p className="text-sm text-text-muted mt-0.5">
                  Client will receive an email with a secure link to upload their documents (bank
                  statements, P&L, tax returns, etc.)
                </p>
              </div>
            </label>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border-sub">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-border text-text-secondary rounded-lg hover:bg-hover transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !form.client_name || !form.client_email}
              className="flex-1 px-4 py-2.5 bg-brand text-base font-medium rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              data-testid="submit-new-client-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  {form.send_email ? 'Submit & Send Email' : 'Submit Client'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AttorneyWorkDesk() {
  const navigate = useNavigate();
  // Clerk-only cutover (2026-07-22): greeting name comes from the Clerk
  // session (attorneys authenticate via Clerk only — the legacy attorney
  // JWT context is now inert and never populates a name).
  const { user } = useUser();
  const { recents, pinned, pin, unpin, isLoaded, hydrateFromApi, isHydrating } = useRecents();

  // Data state
  const [cases, setCases] = useState<AttorneyCase[]>([]);
  const [submittedClients, setSubmittedClients] = useState<SubmittedClient[]>([]);
  const [deadlineAlerts, setDeadlineAlerts] = useState<DeadlineAlert[]>([]);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [activeTab, setActiveTab] = useState<TabOption>('cases');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);

  // Hydrate recents on mount
  useEffect(() => {
    if (isLoaded) {
      hydrateFromApi();
    }
  }, [isLoaded, hydrateFromApi]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const authHeaders = await getAuthHeaders();

      if (!authHeaders.Authorization) {
        navigate('/attorney/login');
        return;
      }

      // Fetch portfolio data (cases)
      const portfolioRes = await fetch(`${BACKEND_URL}/api/v1/attorney/portfolio`, {
        headers: authHeaders,
      });

      if (portfolioRes.status === 401) {
        localStorage.removeItem('attorney_token');
        localStorage.removeItem('attorney_profile');
        navigate('/attorney/login');
        return;
      }

      if (portfolioRes.ok) {
        const portfolioData = await portfolioRes.json();
        // Backend cases carry claim_id/claimant_name/status:{key,label} —
        // normalize onto the fields this table actually reads (case_id,
        // client_name, status, status_label) without touching anything else.
        //
        // Bug fix (2026-07-23): the backend (`_build_case_payload` in
        // attorney_dashboard_service.py) sends the demand amount as
        // `claim_value: {raw, formatted}`, never a flat `claim_amount` —
        // so the "Demand" column, the highest/lowest-demand sort, and every
        // FinancialValue reading `row.claim_amount` were rendering "—" for
        // every real case (undefined -> FinancialValue's null/undefined
        // branch). Same story for `settlement_position.demand`, which the
        // backend sends as `{raw, formatted}` but this component's type
        // declares (and reads) as a bare number. Both are normalized here.
        const normalizedCases: AttorneyCase[] = (portfolioData.cases || []).map((c: any) => ({
          ...c,
          case_id: c.claim_id ?? c.case_id,
          client_name: c.claimant_name ?? c.client_name,
          status: c.status?.key ?? c.status,
          status_label: c.status?.label ?? c.status_label,
          is_released: c.is_released,
          claim_amount:
            c.claim_amount ?? (typeof c.claim_value === 'object' ? c.claim_value?.raw : c.claim_value) ?? 0,
          claim_amount_formatted:
            c.claim_amount_formatted ?? (typeof c.claim_value === 'object' ? c.claim_value?.formatted : undefined),
          claim_value_label: typeof c.claim_value === 'object' ? c.claim_value?.label ?? null : null,
          claim_value_source: typeof c.claim_value === 'object' ? c.claim_value?.source ?? null : null,
          settlement_position: c.settlement_position
            ? {
                ...c.settlement_position,
                demand:
                  typeof c.settlement_position.demand === 'object'
                    ? c.settlement_position.demand?.raw ?? 0
                    : c.settlement_position.demand ?? 0,
              }
            : c.settlement_position,
        }));
        setCases(normalizedCases);
        setSummary(portfolioData.portfolio_summary || null);
      }

      // Fetch submitted clients
      const clientsRes = await fetch(`${BACKEND_URL}/api/v1/attorney/clients`, {
        headers: authHeaders,
      });
      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setSubmittedClients(clientsData.clients || []);
      }

      // Fetch deadline alerts
      const alertsRes = await fetch(`${BACKEND_URL}/api/v1/attorney/dashboard/alerts`, {
        headers: authHeaders,
      });
      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        setDeadlineAlerts(alertsData.deadline_alerts || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate metrics
  const metrics = useMemo(() => {
    // Every accumulator coerces through Number(...) || 0 so a missing/NaN
    // field on a real case (d6d88aea has null demand / days_open) can never
    // poison the metric into "$NaN" / "NaN".
    const num = (v: unknown) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    // KNOWN BACKEND GAP (verified against attorney_dashboard_service.py,
    // 2026-07-23): the portfolio endpoint never emits a `ready_for_demand`
    // status key — `_STATUS_LABELS` there is intake/uploaded/processing/
    // ready/review/complete/action_needed/in_preparation — `ready_for_demand`
    // was never one of them, so matching on the status string always yielded
    // 0. The portfolio payload now carries the real boolean (computed in
    // attorney_case_builder.py's case-strength logic), so read that.
    const demandsOutstanding = cases.filter((c) => c.ready_for_demand).length;
    const settlementPipeline = cases.reduce(
      (sum, c) => sum + num(c.settlement_position?.demand),
      0
    );
    const avgDaysToDemand =
      cases.length > 0
        ? Math.round(cases.reduce((sum, c) => sum + num(c.days_open), 0) / cases.length)
        : 0;

    return [
      {
        key: 'active_matters',
        label: 'Active Matters',
        value: num(summary?.total_cases ?? cases.length),
        subtitle: `${num(summary?.cases_ready_for_demand ?? demandsOutstanding)} need next action`,
      },
      {
        key: 'demands_outstanding',
        label: 'Demands Outstanding',
        value: demandsOutstanding,
        subtitle: 'response action needed',
        accent: true, // Orange accent for needs-action
      },
      {
        key: 'settlement_pipeline',
        label: 'Settlement Pipeline',
        // <FinancialValue> (mono, zero-as-"—") instead of the hand-rolled
        // Intl.NumberFormat string this used to render.
        value: <FinancialValue value={settlementPipeline} size="lg" />,
        subtitle: 'weighted across matters',
      },
      {
        key: 'avg_days_to_demand',
        label: 'Avg Days-to-Demand',
        value: num(summary?.average_case_age_days ?? avgDaysToDemand),
        subtitle: 'case aging metric',
      },
    ];
  }, [cases, summary]);

  // Filter and sort cases
  const filteredCases = useMemo(() => {
    let result = [...cases];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.client_name?.toLowerCase().includes(query) ||
          c.business_name?.toLowerCase().includes(query) ||
          c.event_type?.toLowerCase().includes(query)
      );
    }

    // Event type filter
    if (filterBy !== 'all') {
      result = result.filter((c) => {
        const eventType = c.event_type?.toLowerCase() || '';
        switch (filterBy) {
          case 'fire':
            return eventType.includes('fire');
          case 'water':
            return eventType.includes('water') || eventType.includes('flood');
          case 'storm':
            return (
              eventType.includes('storm') ||
              eventType.includes('hurricane') ||
              eventType.includes('wind')
            );
          case 'other':
            return !['fire', 'water', 'flood', 'storm', 'hurricane', 'wind'].some((t) =>
              eventType.includes(t)
            );
          default:
            return true;
        }
      });
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.event_date || 0).getTime() - new Date(a.event_date || 0).getTime();
        case 'oldest':
          return new Date(a.event_date || 0).getTime() - new Date(b.event_date || 0).getTime();
        case 'highest':
          return (b.claim_amount || 0) - (a.claim_amount || 0);
        case 'lowest':
          return (a.claim_amount || 0) - (b.claim_amount || 0);
        case 'name':
          return (a.client_name || '').localeCompare(b.client_name || '');
        default:
          return 0;
      }
    });

    return result;
  }, [cases, searchQuery, sortBy, filterBy]);

  // DataTable columns
  const columns = useMemo(
    () => [
      { key: 'client_name', label: 'Client / Business', sortable: true, width: '25%' },
      { key: 'claim_amount', label: 'Claim Value', sortable: true, width: '15%' },
      { key: 'status_label', label: 'Status', sortable: true, width: '15%' },
      { key: 'next_action', label: 'Next Action', sortable: false, width: '25%' },
      { key: 'days_open', label: 'Days Open', sortable: true, width: '10%' },
      { key: 'actions', label: '', sortable: false, width: '10%' },
    ],
    []
  );

  // Render cell functions
  //
  // Color-spec fix (2026-07-23): the previous mapping compared `row.status`
  // against 'ready_for_demand' / 'in_review', neither of which the portfolio
  // endpoint ever sends (verified against `_STATUS_LABELS` in
  // attorney_dashboard_service.py: intake / uploaded / processing / ready /
  // review / complete / action_needed / in_preparation) — so every real case
  // fell through to 'neutral' regardless of actual status. Worse, the dead
  // 'in_review' branch mapped to the 'complete' (green) badge variant, which
  // would have painted an in-progress case green — green is reserved for a
  // true done state per the design spec. Rewired to the real status
  // vocabulary: only the genuinely-done 'complete' status gets green.
  const renderClientCell = (value: string, row: AttorneyCase) => (
    <div className="min-w-0">
      <div>{value}</div>
      <HandoffLine handoff={row.handoff} viewerSeat="attorney" />
    </div>
  );

  const renderStatusCell = (value: string, row: AttorneyCase) => {
    let variant: 'waiting' | 'pending' | 'in_progress' | 'complete' | 'attention' | 'neutral';
    if (row.is_released === false) {
      variant = 'waiting';
    } else {
      switch (row.status) {
        case 'complete':
          variant = 'complete'; // green — true done state only
          break;
        case 'action_needed':
          variant = 'attention'; // orange — needs attorney action
          break;
        case 'ready':
          variant = 'pending'; // orange — analysis ready, next step pending
          break;
        case 'review':
          variant = 'in_progress'; // orange — under review, not yet done
          break;
        default:
          variant = 'neutral'; // gray — intake / uploaded / processing / in_preparation
      }
    }
    return <StatusBadge text={value} variant={variant} size="sm" />;
  };

  const renderValueCell = (value: number, row: AttorneyCase) => {
    return (
      <div>
        <FinancialValue value={value} size="md" />
        {row.claim_value_label && (
          <div
            className="text-xs text-text-muted mt-0.5 whitespace-nowrap"
            title={row.claim_value_source === 'claimed' ? 'Legacy claimed value — no engine net yet' : undefined}
          >
            {row.claim_value_source === 'claimed' && (
              <span className="sr-only">Legacy claimed value — no engine net yet</span>
            )}
            {row.claim_value_label}
          </div>
        )}
      </div>
    );
  };

  const renderActionsCell = (_: unknown, row: AttorneyCase) => {
    const preRelease = row.is_released === false;
    return (
      <div className="flex items-center justify-end gap-1">
        {!preRelease && row.wizard_url && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(row.wizard_url!);
            }}
            className="p-1.5 hover:bg-hover rounded transition-colors"
            title="Open in Wizard"
          >
            <FileText className="w-4 h-4 text-text-muted" />
          </button>
        )}
        {!preRelease && row.documents_url && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(row.documents_url!);
            }}
            className="p-1.5 hover:bg-hover rounded transition-colors"
            title="View Documents"
          >
            <FolderOpen className="w-4 h-4 text-text-muted" />
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(row.primary_action_url || `/attorney/case/${row.case_id}`);
          }}
          className="p-1.5 hover:bg-hover rounded transition-colors"
          title="View Case"
        >
          <ExternalLink className="w-4 h-4 text-brand" />
        </button>
      </div>
    );
  };

  const handleCaseClick = (caseItem: AttorneyCase) => {
    navigate(`/attorney/case/${caseItem.case_id}`);
  };

  const handleGeneratorNavigate = (route: string, caseId?: string) => {
    // A generator MUST be scoped to an explicit case. The old `/attorney/${route}`
    // fallback produced a caseless generator (and, upstream, silently defaulted to
    // cases[0] → wrong-client documents). Refuse to navigate without a case rather
    // than route to an unscoped or wrong target. The sidebar already gates the
    // buttons on an explicit selection; this is defense-in-depth.
    if (!caseId) return;
    navigate(`/attorney/case/${caseId}/${route}`);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const CLIENT_STATUS_LABELS: Record<string, string> = {
    pending_documents: 'Pending Documents',
    submitted: 'Submitted',
    processing: 'Processing',
    completed: 'Completed',
  };

  const clientStatusLabel = (status: string): string => {
    return CLIENT_STATUS_LABELS[status] || status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="min-h-screen bg-base" data-testid="attorney-workdesk">
      <WorkDeskUtilityBar
        roleLabel="Attorney WorkDesk"
        onRefresh={fetchData}
        isRefreshing={isLoading}
        roleTag="ATTORNEY"
      />
      {/* Header */}
      <div className="px-6 py-5 border-b border-hairline">
        <div className="max-w-[1320px] mx-auto flex items-center justify-between">
          <div>
            <div className="text-xs tracking-[1.4px] text-brand uppercase font-semibold mb-1">
              Attorney · Work Desk
            </div>
            <h1 className="font-serif text-2xl text-text-primary">
              {greeting()}, {user?.firstName || user?.fullName?.split(' ')[0] || 'Attorney'}
            </h1>
            <p className="mt-1 text-sm text-text-muted">
              {cases.filter((c) => c.status === 'ready_for_demand').length > 0 && (
                <>
                  <strong className="text-brand">
                    {cases.filter((c) => c.status === 'ready_for_demand').length} demand
                    {cases.filter((c) => c.status === 'ready_for_demand').length === 1
                      ? ' is'
                      : 's are'}
                  </strong>{' '}
                  due soon and{' '}
                </>
              )}
              <strong className="text-text-primary">
                {cases.filter((c) => c.status === 'in_review').length} matter
                {cases.filter((c) => c.status === 'in_review').length === 1 ? '' : 's'}
              </strong>{' '}
              need{cases.filter((c) => c.status === 'in_review').length === 1 ? 's' : ''} your
              attention.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/attorney/letterhead')}
              className="flex items-center gap-2 px-4 py-2.5 bg-surface border border-border text-text-secondary font-medium rounded-lg hover:bg-hover transition-colors"
              data-testid="my-letterhead-btn"
              title="Set the name and bar number printed on your demand letters and Proofs of Loss"
            >
              <FileSignature className="w-4 h-4" />
              My Letterhead
            </button>
            <button
              onClick={() => setShowNewClientModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand text-base font-semibold rounded-lg hover:bg-brand-hover transition-colors"
              data-testid="new-client-btn"
            >
              <UserPlus className="w-5 h-5" />
              New Client
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        <div className="max-w-[1320px] mx-auto">
          <div className="flex gap-6">
            {/* Left/Main Area */}
            <div className="flex-1 min-w-0 space-y-6">
              {/* Metric Rail */}
              <MetricRail
                metrics={metrics}
                isLoading={isLoading}
              />

              {/* Deadline Alerts */}
              {deadlineAlerts.length > 0 && (
                <DeadlineAlerts
                  alerts={deadlineAlerts}
                  onNavigate={(caseId) => navigate(`/attorney/case/${caseId}`)}
                />
              )}

              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('cases')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'cases'
                      ? 'bg-brand text-base'
                      : 'bg-surface text-text-secondary border border-border hover:bg-hover'
                  }`}
                  data-testid="tab-cases"
                >
                  Cases ({cases.length})
                </button>
                <button
                  onClick={() => setActiveTab('clients')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'clients'
                      ? 'bg-brand text-base'
                      : 'bg-surface text-text-secondary border border-border hover:bg-hover'
                  }`}
                  data-testid="tab-clients"
                >
                  Submitted Clients ({submittedClients.length})
                </button>
              </div>

              {/* Cases Tab */}
              {activeTab === 'cases' && (
                <div className="rounded-[13px] border border-border bg-surface overflow-hidden">
                  {/* Search & Filter Header */}
                  <div className="px-6 py-4 border-b border-border-sub">
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Search */}
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-dim" />
                        <input
                          type="text"
                          placeholder="Search by client, business, or event type..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-10 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
                          data-testid="case-search-input"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-dim hover:text-text-secondary"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Sort */}
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as SortOption)}
                        className="px-4 py-2.5 border border-border rounded-lg bg-elevated text-text-primary focus:ring-2 focus:ring-brand"
                        data-testid="case-sort-select"
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="highest">Highest Value</option>
                        <option value="lowest">Lowest Value</option>
                        <option value="name">By Name</option>
                      </select>

                      {/* Filter Toggle */}
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${
                          filterBy !== 'all'
                            ? 'border-brand bg-brand/5 text-brand'
                            : 'border-border hover:bg-hover'
                        }`}
                      >
                        <Filter className="w-4 h-4" />
                        <span>Filter</span>
                        {filterBy !== 'all' && <span className="w-2 h-2 bg-brand rounded-full" />}
                      </button>

                      {/* Refresh */}
                      <button
                        onClick={fetchData}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-4 py-2.5 text-text-secondary hover:text-text-primary hover:bg-hover rounded-lg transition-colors"
                      >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                      </button>
                    </div>

                    {/* Filter Options */}
                    {showFilters && (
                      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border-sub">
                        <span className="text-sm text-text-muted mr-2">Event Type:</span>
                        {[
                          { value: 'all', label: 'All' },
                          { value: 'fire', label: 'Fire' },
                          { value: 'water', label: 'Water/Flood' },
                          { value: 'storm', label: 'Storm/Hurricane' },
                          { value: 'other', label: 'Other' },
                        ].map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setFilterBy(option.value as FilterOption)}
                            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                              filterBy === option.value
                                ? 'bg-brand text-base'
                                : 'bg-elevated text-text-secondary hover:bg-hover'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* DataTable */}
                  {isLoading ? (
                    <div className="h-48 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-brand" />
                    </div>
                  ) : error ? (
                    <div className="p-12 text-center">
                      <AlertCircle className="w-10 h-10 text-status-active mx-auto mb-4" />
                      <p className="text-status-active font-medium">{error}</p>
                      <button
                        onClick={fetchData}
                        className="mt-4 px-4 py-2 bg-elevated hover:bg-hover rounded-lg text-sm font-medium transition-colors"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : filteredCases.length === 0 ? (
                    <EmptyState
                      icon={<Briefcase className="w-12 h-12" />}
                      title={cases.length === 0 ? 'No active cases' : 'No matching cases'}
                      description={
                        cases.length === 0
                          ? 'Cases will appear here when they are assigned to you.'
                          : 'No cases match your search or filter criteria. Try adjusting your filters.'
                      }
                    />
                  ) : (
                    <DataTable
                      columns={columns.map((col) => ({
                        ...col,
                        render:
                          col.key === 'status_label'
                            ? renderStatusCell
                            : col.key === 'claim_amount'
                              ? renderValueCell
                              : col.key === 'actions'
                                ? renderActionsCell
                                : col.key === 'client_name'
                                  ? renderClientCell
                                  : undefined,
                      }))}
                      data={filteredCases}
                      onRowClick={handleCaseClick}
                      rowTitle={(row) =>
                        row.is_released === false
                          ? 'Draft — report not yet released. Progress is viewable; the report is a draft until attested.'
                          : undefined
                      }
                      emptyMessage="No cases found"
                    />
                  )}
                </div>
              )}

              {/* Submitted Clients Tab */}
              {activeTab === 'clients' && (
                <div className="rounded-[13px] border border-border bg-surface overflow-hidden">
                  <div className="px-6 py-4 border-b border-border-sub">
                    <h3 className="font-semibold text-text-primary">Clients You have Submitted</h3>
                    <p className="text-sm text-text-muted">
                      Track the status of your submitted clients
                    </p>
                  </div>

                  {submittedClients.length === 0 ? (
                    <div className="p-12 text-center">
                      <Users className="w-12 h-12 text-text-dim mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-text-primary mb-2">
                        No Clients Submitted Yet
                      </h3>
                      <p className="text-text-muted mb-4">
                        Click &quot;New Client&quot; to submit your first client for processing.
                      </p>
                      <button
                        onClick={() => setShowNewClientModal(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-base rounded-lg hover:bg-brand-hover"
                      >
                        <UserPlus className="w-4 h-4" />
                        Submit New Client
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-border-faint">
                      {submittedClients.map((client) => (
                        <div key={client.id} className="px-6 py-4 hover:bg-hover transition-colors">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-text-primary">
                                {client.claimant_name}
                              </h4>
                              <div className="flex items-center gap-4 mt-1 text-sm text-text-muted">
                                <span>{client.client_email}</span>
                                {client.business_name && (
                                  <span>· {client.business_name}</span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <StatusBadge
                                text={clientStatusLabel(client.status)}
                                variant={
                                  client.status === 'completed'
                                    ? 'complete'
                                    : client.status === 'processing'
                                      ? 'warning'
                                      : 'neutral'
                                }
                                size="sm"
                              />
                              <p className="text-xs text-text-dim mt-1">
                                {formatDate(client.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="w-72 flex-shrink-0 space-y-6">
              <GeneratorsSidebar cases={cases} onNavigate={handleGeneratorNavigate} />
              <RecentsRail
                recents={recents}
                pinned={pinned}
                onPin={pin}
                onUnpin={unpin}
                isLoading={isHydrating}
              />
            </div>
          </div>
        </div>
      </div>

      {/* New Client Modal */}
      <NewClientModal
        isOpen={showNewClientModal}
        onClose={() => setShowNewClientModal(false)}
        onSuccess={fetchData}
      />
    </div>
  );
}
